import React from 'react';
import {
    Menu, Bell, Bookmark, Heart, User, Zap, Share2, Globe,
    Lightbulb, Fingerprint, Download, Settings, LogOut, Users,
    LifeBuoy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { ProBadge } from '../../components/ProBadge';
import { toast } from 'sonner';
import { CategorySidebar } from './CategorySidebar';

const API = '/api';
const ADMIN_EMAILS = ['nimrod.lev@gmail.com', 'nimrod@brainsync.pro'];

// ─── DashboardHeader ───────────────────────────────────────────────
// Sticky top header. Left group is the mobile menu button (opens the
// CategorySidebar in a sheet) + logo. Center is the brand wordmark
// with a scroll-aware fade. Right group is: bookmark/favorite counts,
// Ask BrainSync shortcut, notifications bell + dropdown, and the user avatar
// dropdown (subscription, share, admin, sign out).
//
// Props are grouped by concern to make the call site scannable. All
// state comes from Dashboard.js — this component is presentational
// (no fetches of its own).
export function DashboardHeader({
    // Navigation + brand fade
    navigate,
    brandScrollProgress,
    headerGreeting,

    // Mobile menu + sidebar (all sidebar props are re-forwarded)
    mobileMenuOpen,
    setMobileMenuOpen,
    sidebarProps,

    // Selection reset (logo click)
    setSelectedCategory,
    setSelectedSubcategory,
    setShowFavorites,

    // Stats
    totalCount,
    favoritesCount,

    // Notifications
    unreadCount,
    notifOpen,
    notifications,
    handleOpenNotifications,
    closeNotifications,

    // User menu
    user,
    subscriptionInfo,
    setUpgradeModalOpen,
    shareMyBrainSync,
    setShareDemoModalOpen,
    setUsernameModalOpen,
    setReferralsModalOpen,
    biometric,
    isDesktopChrome,
    handleLogout,
}) {
    const goHome = () => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setShowFavorites(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-border">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-1 lg:gap-3">
                {/* Left side - Mobile menu + Logo */}
                <div className="flex items-center gap-3 shrink-0">
                    {/* Mobile Menu Button */}
                    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="sm" className="lg:hidden h-9 w-9 p-0" data-testid="mobile-menu-btn">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-72 p-4 flex flex-col">
                            <SheetHeader className="mb-6 flex-shrink-0">
                                <SheetTitle className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-lg overflow-hidden">
                                        <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                                    </div>
                                    <span className="font-heading font-bold">BrainSync</span>
                                </SheetTitle>
                            </SheetHeader>
                            <div className="flex-1 overflow-y-auto -mx-1 px-1">
                                <CategorySidebar isMobile={true} {...sidebarProps} />
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* Logo button — hidden on mobile/tablet (< lg). */}
                    <button
                        type="button"
                        onClick={goHome}
                        className="hidden lg:block cursor-pointer bg-transparent border-0 p-0 shrink-0"
                        data-testid="header-logo"
                        aria-label="BrainSync home"
                    >
                        <div className="w-9 h-9 rounded-lg overflow-hidden shadow-md shadow-primary/20">
                            <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                        </div>
                    </button>
                </div>

                {/* Brand wordmark + tagline (flush-left, flex-1 wrapper). */}
                <div className="flex-1 min-w-0 flex justify-start">
                    <button
                        type="button"
                        onClick={goHome}
                        className="flex flex-col items-start sm:flex-row sm:items-baseline sm:gap-2.5 leading-tight bg-transparent border-0 p-0 min-w-0 transition-[opacity,transform] duration-200 will-change-[opacity,transform]"
                        data-testid="header-brand"
                        style={{
                            // Bottoms out at 0.45 opacity / 0.92 scale.
                            opacity: 1 - 0.55 * brandScrollProgress,
                            transform: `scale(${1 - 0.08 * brandScrollProgress})`,
                            transformOrigin: 'left center',
                        }}
                    >
                        <span className="font-heading font-bold text-xl sm:text-2xl">
                            Brain<span className="text-emerald-500">Sync</span>
                        </span>
                        <span
                            className="hidden lg:inline text-[12px] sm:text-[13px] text-muted-foreground truncate max-w-[180px] sm:max-w-none sm:whitespace-nowrap sm:before:content-['·'] sm:before:mr-2 sm:before:text-zinc-400"
                            data-testid="header-greeting"
                        >
                            {headerGreeting}
                        </span>
                    </button>
                </div>

                {/* Right side - Stats + Notifications + User menu */}
                <div className="flex items-center gap-2 shrink-0">
                    <div className="hidden sm:flex items-center gap-1.5">
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(16,185,129,0.06)' }}>
                            <Bookmark className="w-3 h-3" style={{ color: '#10b981' }} />
                            <span className="text-xs font-semibold" style={{ color: '#10b981' }}>{totalCount}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.04)' }}>
                            <Heart className="w-3 h-3" style={{ color: '#ef4444' }} />
                            <span className="text-xs font-semibold" style={{ color: '#ef4444' }}>{favoritesCount}</span>
                        </div>
                    </div>

                    {/* Notifications Bell */}
                    <div className="relative" data-testid="notifications-bell">
                        <button
                            onClick={handleOpenNotifications}
                            className="relative p-1.5 rounded-lg hover:bg-secondary transition-colors"
                            data-testid="notifications-bell-btn"
                        >
                            <Bell className="w-5 h-5 text-muted-foreground" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="unread-badge">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                        {notifOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={closeNotifications} />
                                <div className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-xl border border-border/80 z-50" data-testid="notifications-dropdown">
                                    <div className="px-4 py-3 border-b border-border/60">
                                        <h3 className="text-sm font-semibold">Notifications</h3>
                                    </div>
                                    {notifications.length === 0 ? (
                                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                            No notifications yet
                                        </div>
                                    ) : (
                                        notifications.map((n) => (
                                            <button
                                                key={n.notification_id}
                                                className={`w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors border-b border-border/30 last:border-0 ${!n.is_read ? 'bg-primary/[0.03]' : ''}`}
                                                onClick={() => {
                                                    closeNotifications();
                                                    if (n.category_id) setSelectedCategory(n.category_id);
                                                }}
                                                data-testid={`notification-${n.notification_id}`}
                                            >
                                                <div className="flex items-start gap-2.5">
                                                    {n.actor_picture ? (
                                                        <img src={n.actor_picture} alt="" className="w-7 h-7 rounded-full flex-shrink-0 mt-0.5" referrerPolicy="no-referrer" />
                                                    ) : (
                                                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                            <Users className="w-3.5 h-3.5 text-primary" />
                                                        </div>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-xs font-medium text-foreground leading-tight">{n.title}</p>
                                                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                                                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                                                            {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* User avatar dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="flex items-center gap-2 rounded-full h-9 px-2" data-testid="user-menu-btn">
                                {user?.picture ? (
                                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                )}
                                <span className="hidden sm:inline text-sm font-medium max-w-[100px] truncate">{user?.name?.split(' ')[0]}</span>
                                <ProBadge tier={subscriptionInfo.tier} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem className="text-muted-foreground text-xs">
                                {user?.email}
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs py-1 cursor-default" data-testid="bookmark-usage-display">
                                <span className="text-muted-foreground">
                                    {(() => {
                                        const used = subscriptionInfo.premium_used ?? 0;
                                        const limit = subscriptionInfo.premium_limit ?? subscriptionInfo.bookmark_limit ?? 20;
                                        if (limit === -1) return `${used} Premium AI saves`;
                                        return `${used} / ${limit} Premium AI saves`;
                                    })()}
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {subscriptionInfo.tier === 'free' ? (
                                <DropdownMenuItem onClick={() => setUpgradeModalOpen(true)} className="cursor-pointer text-primary font-medium" data-testid="upgrade-menu-btn">
                                    <Zap className="w-4 h-4 mr-2" />
                                    Upgrade to Pro
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem onClick={async () => {
                                    try {
                                        const res = await fetch(`${API}/subscriptions/portal`, { credentials: 'include' });
                                        if (res.ok) {
                                            const d = await res.json();
                                            if (d.url) window.location.href = d.url;
                                            else toast.info(d.message || 'Your subscription is managed by the BrainSync team.');
                                        } else {
                                            toast.error('Could not open subscription portal');
                                        }
                                    } catch (e) {
                                        console.error('Portal error:', e);
                                        toast.error('Could not open subscription portal');
                                    }
                                }} className="cursor-pointer" data-testid="manage-subscription-btn">
                                    <Zap className="w-4 h-4 mr-2" />
                                    Manage Subscription
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={shareMyBrainSync} className="cursor-pointer" data-testid="share-my-brainsync-menu-btn">
                                <Share2 className="w-4 h-4 mr-2 text-emerald-600" />
                                Share my BrainSync
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShareDemoModalOpen(true)} className="cursor-pointer" data-testid="share-demo-refresher-menu-btn">
                                <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                                How to share from any app
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setUsernameModalOpen(true)} className="cursor-pointer" data-testid="public-profile-menu-btn">
                                <Globe className="w-4 h-4 mr-2" />
                                Public profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setReferralsModalOpen(true)} className="cursor-pointer" data-testid="referrals-menu-btn">
                                <Share2 className="w-4 h-4 mr-2" />
                                Invite friends
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {biometric.isSupported && (
                                <DropdownMenuItem
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await biometric.setEnabled(!biometric.enabled);
                                    }}
                                    className="cursor-pointer"
                                    data-testid="biometric-toggle-menu-item"
                                >
                                    <Fingerprint className="w-4 h-4 mr-2" />
                                    {biometric.enabled ? 'Disable App Lock' : 'Enable App Lock'}
                                </DropdownMenuItem>
                            )}
                            {isDesktopChrome && (
                                <DropdownMenuItem asChild className="cursor-pointer" data-testid="extension-menu-link">
                                    <a href="/brainsync-extension.zip" download className="flex items-center">
                                        <Download className="w-4 h-4 mr-2" />
                                        Chrome Extension
                                    </a>
                                </DropdownMenuItem>
                            )}
                            {isDesktopChrome && <DropdownMenuSeparator />}
                            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer" data-testid="settings-menu-link">
                                <Settings className="w-4 h-4 mr-2" />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    // Reuse the FeedbackPill modal - same
                                    // form, same context capture, same
                                    // notification pipeline. No mailto
                                    // fragility, no lost triage context.
                                    try { window.dispatchEvent(new CustomEvent('bs:open-feedback')); } catch (_) { /* ignore */ }
                                }}
                                className="cursor-pointer"
                                data-testid="support-menu-link"
                            >
                                <LifeBuoy className="w-4 h-4 mr-2" />
                                Support
                            </DropdownMenuItem>
                            {ADMIN_EMAILS.includes(user?.email) && (
                                <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="admin-menu-link">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Admin Dashboard
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
}
